import {
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControlLabel,
  Box
} from "@mui/material"
import {
  Wrapper,
  HeaderWrapper,
  Header,
  Subheader,
} from "../SettingsSection/SettingsSection.styles"
import {
  ValidationOutcome,
  type OpCoValidationRuleRead,
} from "../../api/generated"
import { useEffect, useState } from "react"
import { useDebounce } from "../../hooks/useDebounce"
import {
  BoldText,
  Text,
  FilterInfoContainer,
  SearchBar,
  NoResults,
} from "./ValidationRules.styles";
import Button from "../Button/Button";
import CustomSwitch from "../../components/Switch/Switch";
import { useAppSelector } from "../../app/hooks"
import { selectLGInternalUser } from "../../activeScreenSlice"

export interface Props {
  validationRules: Array<OpCoValidationRuleRead>
  onChangeOutcome: (
    validationRule: OpCoValidationRuleRead,
    outcome: ValidationOutcome,
  ) => void
  onChangeStatus: (
    validationRule: OpCoValidationRuleRead,
    isEnabled: boolean
  ) => void
}

const AllString = "All"

const outcomeOptions = ["Review", "Reject"].map((option) => (
  <MenuItem key={option} value={option.toLowerCase()}>
    {option}
  </MenuItem>
))

const filterOutcomeOptions = [AllString, "Review", "Reject"].map(
  (option) => (
    <MenuItem key={option} value={option}>
      {option}
    </MenuItem>
  ),
)

const filterStatusOptions = [AllString, "Active", "Inactive"].map(
  (option) => (
    <MenuItem key={option} value={option}>
      {option}
    </MenuItem>
  ),
)

export default function OpCoValidationRules({
  validationRules,
  onChangeOutcome,
  onChangeStatus
}: Props) {
  const uniqueOpcoNames = Array.from(
    new Set(validationRules.map(item => item.opco.name))
  ).sort((a, b) => a.localeCompare(b));

  const [filterText, setFilterText] = useState<string | undefined>()
  const debouncedInputValue = useDebounce(filterText)

  const [filterOpco, setFilterOpco] = useState<string>(AllString)
  const [filterStatus, setFilterStatus] = useState<string>(AllString)
  const LGInternalUser = useAppSelector(selectLGInternalUser);

  const filterOpcoOptions = [AllString, ...uniqueOpcoNames].map((option) => (
    <MenuItem key={option} value={option}>
      {option}
    </MenuItem>
  ))

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>, validationRule) => {
    onChangeStatus(validationRule, event?.target?.checked as boolean);
  };

  const [filterOutcome, setFilterOutcome] = useState<ValidationOutcome | "All">(
    AllString,
  )

  const compareStatus= (status, validationRule) => {
    let validationRuleStatus = validationRule.is_enable? "active":"Inactive";
    return status === AllString || status.toLowerCase() === validationRuleStatus;
  }

  const [filteredValidationRules, setFilteredValidationRules] =
    useState(validationRules)

  useEffect(() => {
    const searchWords = debouncedInputValue
      ? debouncedInputValue.trim().toLowerCase().split(" ").filter(Boolean)
      : []

    setFilteredValidationRules(
      validationRules.filter((validationRule) => {
        const matchesText = searchWords.length
          ? searchWords.every(
              (word) =>
                validationRule.validation_rule.validation_description
                  .toLowerCase()
                  .includes(word) ||
                validationRule.opco.name.toLowerCase().includes(word),
            )
          : true

        const matchesOpco =
          filterOpco === AllString || validationRule.opco.name === filterOpco

        const matchesOutcome =
          filterOutcome === AllString ||
          validationRule.outcome === filterOutcome.toLowerCase()

        const matchedStatus = compareStatus(filterStatus, validationRule);

        return matchesText && matchesOpco && matchesOutcome && matchedStatus
      }),
    )
  }, [
    debouncedInputValue,
    filterOpco,
    filterOutcome,
    validationRules,
    setFilteredValidationRules,
    filterStatus,
  ])

  function handleClearFilters() {
    setFilterText(undefined)
    setFilterOpco(AllString)
    setFilterOutcome(AllString)
    setFilterStatus(AllString)
  }

  const noFilterApplied =
    filterText === undefined &&
    filterOpco === AllString &&
    filterOutcome === AllString && 
    filterStatus === AllString

  return (
    <Wrapper>
      <HeaderWrapper>
        <Header>Validation rules selection</Header>
        <Subheader>
          Define what happens to the invoice when certain validation rules fail
        </Subheader>
      </HeaderWrapper>

      <Table>
        <TableBody>
          <TableRow>
            <TableCell colSpan={4} sx={{ border: 0, paddingBottom: 0 }}>
              <FilterInfoContainer sx={{marginBottom: 0}}>
                <Text>Showing</Text>
                {!noFilterApplied && (
                  <BoldText>{filteredValidationRules.length}</BoldText>
                )}
                {!noFilterApplied && <Text>filtered results of</Text>}
                <BoldText>{validationRules.length}</BoldText>
                <Text>validation rules</Text>
                {!noFilterApplied && (
                  <Button
                    label="Clear filters"
                    onClick={handleClearFilters}
                    color="--black"
                    colorSecondary="--black"
                    sx={{ marginLeft: "10px" }}
                  />
                )}
              </FilterInfoContainer>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Table>
        <TableHead sx={{borderTop: "1px solid rgba(224, 224, 224, 1)"}}>
          <TableRow>
            <TableCell sx={{ width: "13%" }}>Business Unit Code</TableCell>
            <TableCell sx={{ width: "50%" }}>Validation Rule</TableCell>
            <TableCell sx={{ width: "15%" }}>Status</TableCell>
            <TableCell sx={{ width: "15%" }}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableHead>
          <TableRow sx={{ border: 0 }}>
            <TableCell sx={{ width: "13%" }}>
              <Select
                sx={{ minWidth: "100px", width: "85%" }}
                value={filterOpco}
                onChange={({ target: { value } }) => {
                  setFilterOpco(value)
                }}
              >
                {filterOpcoOptions}
              </Select>
            </TableCell>
            
            <TableCell sx={{ width: "50%" }}>
              <SearchBar
                autoFocus={true}
                value={filterText || ""}
                onChange={(event) => {
                  setFilterText(event.target.value)
                }}
                label="Enter keywords to search on validation rules"
              />
            </TableCell>
            <TableCell sx={{ width: "15%" }}>
              <Select
                sx={{ minWidth: "100px", width: "85%"}}
                value={filterStatus}
                onChange={({ target: { value } }) => {
                  setFilterStatus(value as string | "All")
                }}
              >
                {filterStatusOptions}
              </Select>

            </TableCell>
            <TableCell sx={{ width: "15%" }}>
              <Select
                sx={{ minWidth: "100px", width: "85%"}}
                value={filterOutcome}
                onChange={({ target: { value } }) => {
                  setFilterOutcome(value as ValidationOutcome | "All")
                }}
              >
                {filterOutcomeOptions}
              </Select>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredValidationRules.length > 0 ? (
            filteredValidationRules.map((validationRule, index) => (
              <TableRow key={validationRule.id} sx={{background: index % 2 === 0 ? "rgba(0, 0, 0, 0.04)":''}}>
                <TableCell>{validationRule.opco.name}</TableCell>
                <TableCell>
                  {validationRule.validation_rule.validation_description}
                </TableCell>
                <TableCell>
                  <FormControlLabel control={<CustomSwitch
                    checked={validationRule.is_enable}
                    onChange={(e) => handleStatusChange(e, validationRule)}
                    inputProps={{ 'aria-label': 'controlled' }}
                    disabled = {LGInternalUser}
                  />} label={<Box component="div" fontSize={14}>
                    {validationRule.is_enable? "Active": "Inactive"}
                  </Box>} 
                />
                </TableCell>
                <TableCell>
                  <Select
                    sx={{ minWidth: "100px", width: "85%" }}
                    disabled={!validationRule.is_enable || LGInternalUser}
                    value={validationRule.outcome}
                    onChange={({ target: { value } }) => {
                      onChangeOutcome(
                        validationRule,
                        value as ValidationOutcome,
                      )
                    }}
                  >
                    {outcomeOptions}
                  </Select>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} sx={{ border: 0 }}>
                <NoResults>
                  There are no validation rules matching your filter.
                </NoResults>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Wrapper>
  )
}
